import { motion } from 'framer-motion';
import type { MotionStyle } from 'framer-motion';
import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '@/lib/utils';

// framer-motion redefines these handlers with animation-specific signatures,
// which conflict with the standard DOM event handler types from HTMLAttributes.
type MotionConflictingHandlers =
  | 'onAnimationStart'
  | 'onAnimationEnd'
  | 'onAnimationIteration'
  | 'onDrag'
  | 'onDragStart'
  | 'onDragEnd';

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, MotionConflictingHandlers> {
  /** Add default internal padding (skip when composing header/body). */
  padded?: boolean;
  /** Hover elevation + pointer cursor for clickable cards. */
  interactive?: boolean;
}

export function Card({
  padded = false,
  interactive = false,
  className,
  children,
  style,
  ...rest
}: CardProps): JSX.Element {
  const motionProps = interactive
    ? {
        whileHover: { y: -2, scale: 1.01 },
        transition: { type: 'spring' as const, stiffness: 400, damping: 25 },
      }
    : {};

  return (
    <motion.div
      className={cn(
        'bg-card text-card-foreground border border-border ring-1 ring-foreground/10 rounded-3xl overflow-hidden transition-all duration-200',
        padded && 'p-5',
        interactive && 'cursor-pointer hover:border-primary/40 hover:ring-foreground/20',
        className,
      )}
      {...(style !== undefined ? { style: style as MotionStyle } : {})}
      {...rest}
      {...motionProps}
    >
      {children}
    </motion.div>
  );
}

export function CardHeader({
  title,
  subtitle,
  action,
  icon,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between p-5 pb-2">
      <div className="flex items-center gap-3">
        {icon && <div className="text-muted-foreground">{icon}</div>}
        <div className="flex flex-col">
          <span className="font-display font-medium text-lg text-foreground tracking-tight">
            {title}
          </span>
          {subtitle !== undefined && (
            <span className="text-xs text-muted-foreground">{subtitle}</span>
          )}
        </div>
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

export function CardBody({
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div className={cn('p-5 pt-3', className)} {...rest}>
      {children}
    </div>
  );
}
